import { useEffect, useState } from 'react';
import { api, type CommerceOrgRow } from '../api';
import { useLoad } from '../lib';
import { ShipToEditor } from '../shipto';

// Any camp's delivery address — view where its purchase orders ship and where that address came from,
// and (admin/ops) record a new one — plus the camps that cannot check out at all because they have
// none. beta-plan §5: that shortfall is to be caught while screening camps, not by a buyer at checkout.
export function ShipToPanel({ canEdit }: { canEdit: boolean }) {
  const [search, setSearch] = useState('');
  const [orgs, setOrgs] = useState<CommerceOrgRow[]>([]);
  const [picked, setPicked] = useState<CommerceOrgRow | null>(null);
  const { data: missing, err, reload } = useLoad(() => api.commerceShipToMissing());

  useEffect(() => {
    const t = setTimeout(() => { api.commerceOrgs(search).then(setOrgs).catch(() => {}); }, 200);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div className="stack">
      <h1>Ship-to</h1>
      <p className="sub">A camp's delivery address. Every purchase order the camp places ships here, and checkout refuses a camp without one. Until staff record one, a camp's own order history supplies it; a camp onboarded here has no history, so it has only what staff record.</p>
      <section className="card">
        <label className="field"><span>Camp</span>
          <input className="search" placeholder="search camps…" value={search} onChange={(e) => { setSearch(e.target.value); setPicked(null); }} />
          {search && !picked && (
            <div className="typeahead">
              {orgs.slice(0, 8).map((o) => <button key={o.id} className="ta-opt" onClick={() => { setPicked(o); setSearch(o.name); }}>{o.name} <span className="mono">#{o.id}</span></button>)}
              {orgs.length === 0 && <div className="loading" style={{ padding: 8 }}>No camps match.</div>}
            </div>
          )}
        </label>
        {picked && <div style={{ marginTop: 12 }}><ShipToEditor key={picked.id} orgId={picked.id} name={picked.name} canEdit={canEdit} onSaved={reload} /></div>}
      </section>
      {err && <p className="msg bad">⚠ {err}</p>}
      <section className="card">
        <h2>Camps that cannot check out{missing ? ` (${missing.length})` : ''}</h2>
        <p className="muted">Active camps with no delivery address on file and none in their order history. Their buyers can build guides and fill a cart, but every checkout is refused until an address is recorded.</p>
        <table>
          <thead><tr><th>Org</th><th>Camp</th><th>Source</th><th></th></tr></thead>
          <tbody>
            {(missing ?? []).map((c) => (
              <tr key={c.id}>
                <td className="mono">{c.id}</td>
                <td>{c.name}</td>
                <td>{c.onboard_status ? 'onboarded here' : 'migrated'}</td>
                <td className="actions">
                  <button className="btn-sm ghost" onClick={() => { setPicked({ id: c.id, name: c.name }); setSearch(c.name); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                    {canEdit ? 'Add address' : 'View'}
                  </button>
                </td>
              </tr>
            ))}
            {missing && missing.length === 0 && <tr><td colSpan={4} className="loading">Every active camp has a delivery address.</td></tr>}
          </tbody>
        </table>
        {!missing && !err && <p className="loading">Loading…</p>}
      </section>
    </div>
  );
}
